import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { 
  Clock, 
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Trophy,
  ChevronRight,
  Flag
} from "lucide-react";
import QuestionImage from "@/components/QuestionImage";
import UpgradeModal from "@/components/UpgradeModal";
import LockPanel from "@/components/LockPanel";
import TrialPill from "@/components/TrialPill";

const MockExamPage = () => {
  const navigate = useNavigate();
  const [examState, setExamState] = useState("setup"); // setup, running, completed
  const [exam, setExam] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [results, setResults] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [flagged, setFlagged] = useState(new Set());
  const timerRef = useRef(null);

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const data = await api.get("/subjects");
        setSubjects(data.subjects);
      } catch (error) {
        console.error("Failed to fetch subjects:", error);
      }
    };
    fetchSubjects();
  }, []);

  useEffect(() => {
    if (examState === "running" && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleSubmitExam();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [examState]);

  const startExam = async () => {
    setLoading(true);
    try {
      const data = await api.post("/mock-exam/start", {
        num_questions: 30,
        time_limit: 45
      });
      setExam(data);
      setTimeRemaining(data.time_limit * 60);
      setExamState("running");
      toast.success("Exam Started!", {
        description: `${data.num_questions} questions, ${data.time_limit} minutes`
      });
    } catch (error) {
      if (error?.response?.status === 402) {
        setShowUpgrade(true);
      } else {
        console.error("Failed to start exam:", error);
        toast.error("Failed to start exam");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOption = (questionId, optionId) => {
    setAnswers({
      ...answers,
      [questionId]: optionId
    });
  };

  const handleToggleFlag = () => {
    const newFlagged = new Set(flagged);
    if (newFlagged.has(currentIndex)) {
      newFlagged.delete(currentIndex);
    } else {
      newFlagged.add(currentIndex);
    }
    setFlagged(newFlagged);
  };

  const handleSubmitExam = async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setLoading(true);
    try {
      const data = await api.post(`/mock-exam/${exam.exam_id}/submit`, {
        answers: answers
      });
      setResults(data);
      setExamState("completed");
      
      if (data.score >= 70) {
        toast.success("Congratulations!", {
          description: `You scored ${data.score.toFixed(1)}%!`
        });
      } else {
        toast.info("Exam Complete", {
          description: `You scored ${data.score.toFixed(1)}%. Keep practicing!`
        });
      }
    } catch (error) {
      console.error("Failed to submit exam:", error);
      toast.error("Failed to submit exam");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const isTimeWarning = timeRemaining <= 300; // 5 minutes warning

  // Setup Screen
  if (examState === "setup") {
    return (
      <div data-testid="mock-exam-setup" className="max-w-2xl mx-auto space-y-8">
        <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} featureName="Full mock exams" />
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Mock Exam</h1>
          <p className="text-muted-foreground">Simulate the real ADC Part 1 exam experience</p>
          <div className="mt-3"><TrialPill /></div>
        </div>

        {/* Practice-Readiness Trials promo */}
        <button
          data-testid="trials-promo-card"
          onClick={() => navigate("/trials")}
          className="w-full text-left rounded-2xl border border-primary/30 bg-primary/5 p-6 hover:border-primary/60 transition-colors"
        >
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-primary">New: Practice-Readiness Trials</p>
              <p className="text-sm text-muted-foreground mt-1">
                3 full ADC-style simulations - 140 questions, 4 hours, real blueprint weighting. Trial 1 is free.
              </p>
            </div>
            <span className="text-primary text-2xl flex-shrink-0">→</span>
          </div>
        </button>

        <div className="glass rounded-2xl p-8 border border-border space-y-6">
          <h2 className="font-semibold text-lg">Exam Details</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-secondary">
              <p className="text-sm text-muted-foreground mb-1">Questions</p>
              <p className="text-2xl font-bold">30</p>
            </div>
            <div className="p-4 rounded-xl bg-secondary">
              <p className="text-sm text-muted-foreground mb-1">Time Limit</p>
              <p className="text-2xl font-bold">45 min</p>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-warning/10 border border-warning/20">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-warning mt-0.5" />
              <div>
                <p className="font-medium text-sm mb-1">Important</p>
                <p className="text-sm text-muted-foreground">
                  Once started, the timer cannot be paused. Make sure you're ready to complete the full exam.
                </p>
              </div>
            </div>
          </div>

          <Button
            data-testid="start-exam-btn"
            onClick={startExam}
            disabled={loading}
            className="w-full rounded-full py-6 text-lg"
          >
            {loading ? "Starting..." : "Start Exam"}
          </Button>
        </div>
      </div>
    );
  }

  // Completed Screen
  if (examState === "completed" && results) {
    return (
      <div data-testid="mock-exam-results" className="max-w-3xl mx-auto space-y-8">
        <div className="text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${results.score >= 70 ? 'bg-success/20' : 'bg-warning/20'}`}>
            <Trophy className={`w-8 h-8 ${results.score >= 70 ? 'text-success' : 'text-warning'}`} />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Exam Complete!</h1>
          <p className="text-muted-foreground">Here's how you performed</p>
        </div>

        {/* Score */}
        <div className="glass rounded-2xl p-8 border border-border text-center">
          <p className="text-sm text-muted-foreground mb-2">Your Score</p>
          <p className={`text-5xl font-bold mb-2 ${results.score >= 70 ? 'text-success' : results.score >= 50 ? 'text-warning' : 'text-destructive'}`}>
            {results.score.toFixed(1)}%
          </p>
          <p className="text-sm text-muted-foreground">
            {results.correct} / {results.total} correct
          </p>
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20">
            <Zap className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">+{results.xp_earned} XP earned</span>
          </div>
        </div>

        {/* Subject Breakdown — locked for free users under freemium v2 */}
        {results.locked ? (
          <LockPanel
            wall="mock_breakdown"
            title={results.lock_copy?.title || "See exactly where you lost marks with Pro"}
            body={results.lock_copy?.body || "Explanations and your subject breakdown are a Pro feature."}
            cta={results.lock_copy?.cta || "Unlock with Pro"}
          />
        ) : (
        <div className="glass rounded-2xl p-6 border border-border">
          <h2 className="font-semibold mb-4">Performance by Subject</h2>
          <div className="space-y-4">
            {Object.entries(results.subject_results || {}).map(([subjectId, data]) => {
              const subject = subjects.find(s => s.id === subjectId);
              const percentage = (data.correct / data.total) * 100;
              
              return (
                <div key={subjectId}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{subject?.name || subjectId}</span>
                    <span className={`text-sm font-bold ${percentage >= 70 ? 'text-success' : percentage >= 50 ? 'text-warning' : 'text-destructive'}`}>
                      {data.correct}/{data.total} ({percentage.toFixed(0)}%)
                    </span>
                  </div>
                  <Progress value={percentage} className="h-2" />
                </div>
              );
            })}
          </div>
        </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            data-testid="retry-exam-btn"
            onClick={() => {
              setExamState("setup");
              setExam(null);
              setAnswers({});
              setResults(null);
              setCurrentIndex(0);
              setFlagged(new Set());
            }}
            className="rounded-full"
          >
            Try Another Exam
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/dashboard")}
            className="rounded-full border-border"
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Running Exam
  const currentQuestion = exam?.questions?.[currentIndex];
  const questionData = exam?.questions ? 
    (typeof currentQuestion === 'string' ? 
      exam.questions.find(q => q.id === currentQuestion) : 
      currentQuestion) : 
    null;

  // Find the actual question object
  const actualQuestion = exam?.questions?.[currentIndex];

  return (
    <div data-testid="mock-exam-running" className="max-w-3xl mx-auto">
      {/* Timer Header */}
      <div className="sticky top-0 z-10 glass-strong rounded-2xl p-4 mb-6 border border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`exam-timer text-2xl font-bold mono ${isTimeWarning ? 'timer-warning' : ''}`}>
              {formatTime(timeRemaining)}
            </div>
            <span className="text-sm text-muted-foreground">
              {currentIndex + 1} / {exam?.questions?.length || 0}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggleFlag}
              className={flagged.has(currentIndex) ? 'text-warning' : ''}
            >
              <Flag className="w-4 h-4" />
            </Button>
            <Button
              data-testid="submit-exam-btn"
              size="sm"
              variant="destructive"
              onClick={handleSubmitExam}
              className="rounded-full"
            >
              Submit Exam
            </Button>
          </div>
        </div>
        <Progress value={((currentIndex + 1) / (exam?.questions?.length || 1)) * 100} className="h-1 mt-3" />
      </div>

      {/* Question */}
      {actualQuestion && (
        <div className="glass rounded-2xl p-6 md:p-8 border border-border mb-6">
          {/* Subject Tag */}
          <div className="mb-4 flex items-center gap-2">
            <span className="text-xs px-3 py-1 rounded-full bg-primary/20 text-primary">
              {subjects.find(s => s.id === actualQuestion.subject_id)?.name || actualQuestion.subject_id}
            </span>
            {flagged.has(currentIndex) && (
              <span className="text-xs px-3 py-1 rounded-full bg-warning/20 text-warning">
                Flagged
              </span>
            )}
          </div>

          {/* Clinical Vignette */}
          {actualQuestion.clinical_vignette && (
            <div className="p-4 rounded-xl bg-secondary border border-border mb-4">
              <p className="text-sm text-muted-foreground italic">{actualQuestion.clinical_vignette}</p>
            </div>
          )}

          {/* Question Text */}
          <QuestionImage src={actualQuestion.image_url} caption={actualQuestion.image_caption} />
          <h2 className="text-lg md:text-xl font-medium mb-6">{actualQuestion.question_text}</h2>

          {/* Options */}
          <div className="space-y-3">
            {actualQuestion.options?.map((option) => {
              const isSelected = answers[actualQuestion.id] === option.id;
              
              return (
                <button
                  key={option.id}
                  data-testid={`exam-option-${option.id}`}
                  onClick={() => handleSelectOption(actualQuestion.id, option.id)}
                  className={`w-full text-left p-4 rounded-xl border transition-all quiz-option
                    ${isSelected ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'}
                  `}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full border flex items-center justify-center text-sm font-medium
                      ${isSelected ? 'border-primary bg-primary text-white' : 'border-border'}
                    `}>
                      {option.id.toUpperCase()}
                    </span>
                    <span className="flex-1">{option.text}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between gap-3 mb-6">
        <Button
          variant="outline"
          onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
          disabled={currentIndex === 0}
          className="rounded-full"
        >
          Previous
        </Button>
        <Button
          onClick={() => setCurrentIndex(Math.min((exam?.questions?.length || 1) - 1, currentIndex + 1))}
          disabled={currentIndex >= (exam?.questions?.length || 1) - 1}
          className="rounded-full"
        >
          Next
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>

      {/* Question Navigator */}
      <div className="glass rounded-2xl p-4 border border-border">
        <p className="text-sm text-muted-foreground mb-3">Question Navigator</p>
        <div className="flex flex-wrap gap-2">
          {exam?.questions?.map((q, index) => {
            const qId = typeof q === 'string' ? q : q.id;
            const isAnswered = !!answers[qId];
            const isCurrent = index === currentIndex;
            const isFlagged = flagged.has(index);
            
            return (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-10 h-10 rounded-lg text-sm font-medium transition-all
                  ${isCurrent ? 'bg-primary text-white' : 
                    isAnswered ? 'bg-success/20 text-success border border-success/30' : 
                    'bg-secondary text-muted-foreground border border-border'}
                  ${isFlagged ? 'ring-2 ring-amber-500' : ''}
                `}
              >
                {index + 1}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Missing import
import { Zap } from "lucide-react";

export default MockExamPage;
